import {NotificationsSettingsService} from "../services/notificationsSettingsService";
import Vue from 'vue'
import cloneDeep from 'lodash/cloneDeep'

// Helper function to get setting choice with default value
const getSettingChoice = (setting, key, defaultState = true) => {
  return setting ? setting[key] : defaultState
}

// Helper function to get title by locale with fallback
const getTitleByLocale = (titleArray, locale) => {
  return titleArray?.find(item => item.locale === locale)?.value || ''
}

// Helper function to get description by locale with fallback
const getDescriptionByLocale = (descriptionArray, locale) => {
  return descriptionArray?.find(item => item.locale === locale)?.value || ''
}

// Group templates by category
const groupTemplatesByCategory = (templatesArray) => {
  const groupedRawTemplates = {}

  templatesArray.forEach(template => {
    const category = template.category || 'system'
    if (!groupedRawTemplates[category]) {
      groupedRawTemplates[category] = []
    }
    groupedRawTemplates[category].push(template)
  })

  return groupedRawTemplates
}

// Map a single template with all required transformations
const mapSingleTemplate = (template, staff, businessLocale, staffSettings) => {
  const displayName = getTitleByLocale(template.title, businessLocale) ||
                     getTitleByLocale(template.title, 'en') || ''

  const description = getDescriptionByLocale(template.description, businessLocale) ||
                     getDescriptionByLocale(template.description, 'en') || ''

  // Find matching staff setting for this template
  const matchingSetting = staffSettings.find(setting =>
    setting.notification_metadata_uid === template.uid
  )

  return {
    uid: matchingSetting?.uid,
    staff_uid: staff?.uid,
    notification_metadata_uid: template.uid,
    notification_code_name: template.code_name || template.name,
    display_name: displayName,
    description: description,
    push: getSettingChoice(matchingSetting, 'push'),
    pane: getSettingChoice(matchingSetting, 'pane'),
    email: getSettingChoice(matchingSetting, 'email'),
    sms: getSettingChoice(matchingSetting, 'sms', false),
    display_push: template.delivery_channel?.includes('push') || false,
    display_pane: template.delivery_channel?.includes('pane') || false,
    display_email: template.delivery_channel?.includes('email') || false,
    display_sms: template.delivery_channel?.includes('sms') || false,
    category: template.trigger_type,
  }
}

// Set category flags based on existing templates
const setCategoryFlags = (templatesArray, groupedTemplates) => {
  templatesArray.forEach(template => {
    const templateCategory = template.trigger_type
    if (templateCategory === 'client') {
      groupedTemplates.category.client = true
    }
    if (templateCategory === 'system') {
      groupedTemplates.category.system = true
    }
  })
}

// Main function to map and group notification templates
const mapAndGroupNotificationTemplates = (templatesArray, staff, businessLocale, staffSettings = []) => {
  // Filter out notifications that shouldn't be shown in settings
  const filteredTemplates = templatesArray.filter(template => template.configurable_by_staff)

  // Group templates by category first
  const groupedRawTemplates = groupTemplatesByCategory(filteredTemplates)

  const groupedTemplates = {
    category: {},
  }

  // Map templates for each category
  Object.keys(groupedRawTemplates).forEach(category => {
    groupedTemplates[category] = groupedRawTemplates[category].map(template =>
      mapSingleTemplate(template, staff, businessLocale, staffSettings)
    )
  })

  // Set category flags based on templates that exist
  setCategoryFlags(filteredTemplates, groupedTemplates)

  return groupedTemplates
}

const state = {
  isSuccessfullySaved:false,
  usedApps: [],
  notificationTemplates: {},
  notificationTemplatesForDiscard: {},
}

const getters = {
  isSuccessfullySaved: state => state.isSuccessfullySaved,
  allStaffSettingsArray: state => {
    const settingsCategoriesKeys = Object.keys(state.notificationTemplates)
    if(settingsCategoriesKeys.length > 0){
      const categoryValues = settingsCategoriesKeys.map(category => state.notificationTemplates[category])
      const arrayCategories = categoryValues.filter(Array.isArray)
      const flattenedSettings = arrayCategories.reduce((previousValue, currentValue) => previousValue.concat(currentValue), [])
      return flattenedSettings
    }
    return []
  },
  usedApps: state => state.usedApps,
  notificationTemplates: state => state.notificationTemplates,
  changedSettingsAmount: (state, getters) => {
    if (getters.allStaffSettingsArray){
      const changedSettings = getters.allStaffSettingsArray.filter(setting =>
        (setting.changed?.push || setting.changed?.pane || setting.changed?.email || setting.changed?.sms))
      return changedSettings.length
    }
    return 0;
  }
}

const mutations = {
  setIsSuccessfullySaved(state, value){
    state.isSuccessfullySaved = value
  },
  setChangedSetting(state, changedItem){
    Object.keys(state.notificationTemplates).every(category =>{
      if(!Array.isArray(state.notificationTemplates[category])) return true;
      const changedItemIndex = state.notificationTemplates[category].findIndex(currentSetting =>
        currentSetting.notification_metadata_uid === changedItem.notification_metadata_uid)
      if(changedItemIndex > -1) {
        Vue.set(state.notificationTemplates[category], changedItemIndex,{
          ...state.notificationTemplates[category][changedItemIndex],
          ...changedItem,
        })
        return false;
      }
      return true
    })
  },
  setUsedApps(state, usedApps){
    state.usedApps = usedApps
  },
  setNotificationTemplates(state, notificationTemplates){
    state.notificationTemplates = notificationTemplates
    // Create a deep copy for the discard backup so mutations to notificationTemplates don't affect the backup
    state.notificationTemplatesForDiscard = cloneDeep(notificationTemplates)
  },
  resetStaffSettings(state){
    state.notificationTemplates = {}
  },
  discardChanges(state){
    // Restore from backup by creating a new deep copy
    // This ensures the restored state is independent from the backup
    state.notificationTemplates = cloneDeep(state.notificationTemplatesForDiscard)
  }
}

const actions = {
  async fetchUsedApps({commit, rootGetters, state, dispatch}){
    if(state.usedApps.length === 0){
      await dispatch("appMarketModule/loadApps", {} , {root: true})
      let usablityStatusCheck = rootGetters["appMarketModule/appUsabilityStatus"]
      let apps = rootGetters["appMarketModule/apps"]
      const usedApps = apps.filter(app => usablityStatusCheck(app))
      commit('setUsedApps', usedApps)
    }
  },
  async fetchNotificationTemplates({commit, rootGetters}){
    try {
      const rawNotificationTemplates = await NotificationsSettingsService.getAllNotificationTemplates()

      // Extract the notification_templates array from the response
      const templatesArray = rawNotificationTemplates.notification_templates || []

      // Get staff and locale information
      const staff = rootGetters["accountModule/staff"]
      const profile = rootGetters["accountModule/profile"]
      const businessLocale = profile?.business?.locale || 'en'

      // Fetch staff settings for notification preferences
      const staffSettings = await NotificationsSettingsService.getStaffSettings(staff?.uid)

      // Map and group templates using helper function
      const groupedTemplates = mapAndGroupNotificationTemplates(templatesArray, staff, businessLocale, staffSettings)

      commit('setNotificationTemplates', groupedTemplates)
      return groupedTemplates
    } catch (error) {
      console.error('Error fetching notification templates:', error)
      commit('setNotificationTemplates', {})
      return error
    }
  },
  async updateChangedSetting({commit}, payload){
    commit('setChangedSetting', payload)
  },
  async saveSettingsChanges({commit, getters, dispatch}){
    try{
      const isSomethingChange = changed => changed?.push || changed?.pane || changed?.email || changed?.sms;
      const settingsToCreate = await getters.allStaffSettingsArray.filter(setting => !setting.uid && isSomethingChange(setting.changed))
      const settingsToUpdate = await getters.allStaffSettingsArray.filter(setting => setting.uid && isSomethingChange(setting.changed))

      // create
      const createMap = settingsToCreate.map(currentSetting =>  NotificationsSettingsService.createSingleSetting(currentSetting))
      // update
      const updateMap = settingsToUpdate.map( currentSetting => NotificationsSettingsService.updateSingleSetting(currentSetting))

      await Promise.all(createMap.concat(updateMap)).then(returnedValues =>{
        const isSuccessfullySaved = returnedValues.filter(value => value === undefined).length === 0
        commit('setIsSuccessfullySaved', isSuccessfullySaved)
      })
      await dispatch('fetchNotificationTemplates')
    }
    catch (e){
      console.log(e)
    }
  },
  async resetStaffSettings({commit}){
    commit('resetStaffSettings')
  },
  async discardChanges({commit}){
    commit('discardChanges')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
